import React, { useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { useUserProfile } from '../contexts/UserProfileContext';
import profileAvatar from '../assets/profile-avatar.png';
import styles from '../styles/DrawerMenu.module.css';
import FaIcon from './FaIcon';
import { trackAuthCtaClicked, trackUiElementClicked } from '../analytics';
import { GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES } from '../ads/googleInterstitialAttributes';

/**
 * DrawerMenu Component
 * 
 * A responsive side drawer menu with support for sections and subsections.
 * 
 * Props:
 * - isOpen: boolean - Whether the drawer is open
 * - onClose: function - Callback to close the drawer
 * - menuItems: array - Array of menu items/sections
 * - showProfileSection: boolean - Whether to show user profile section (default: true)
 * 
 * menuItems structure:
 * [
 *   {
 *     type: 'section',
 *     label: 'Section Name', // optional header
 *     items: [
 *       { label: 'Item', icon: 'fas fa-icon', href: '/path', onClick: fn },
 *       { 
 *         label: 'With Subsection',
 *         items: [
 *           { label: 'Sub Item', href: '/sub' }
 *         ]
 *       }
 *     ]
 *   },
 *   {
 *     type: 'item',
 *     label: 'Direct Item',
 *     icon: 'fas fa-home',
 *     href: '/'
 *   },
 *   {
 *     type: 'divider'
 *   }
 * ]
 */

const DrawerMenu = ({
  isOpen,
  onClose,
  menuItems = [],
  showProfileSection = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const drawerRef = useRef(null);
  const { isSignedIn, user } = useUser();
  useUserProfile();
  // Note: We intentionally don't show remembered name/email for anonymous users
  // to avoid confusion - they should clearly see they're not logged in
  const hasEverAuthenticated = useHasEverAuthenticated();

  // Navigate to sign-in page with current location as next
  const handleSignInClick = () => {
    const currentPath = location.pathname + location.search;
    trackUiElementClicked({
      source_surface: 'drawer_menu',
      surface: 'drawer_profile',
      element_id: 'sign_in_cta',
      action: 'click',
      target: currentPath,
    });
    trackAuthCtaClicked({
      source_surface: 'drawer_profile',
      gate_reason: 'generic_auth_entry',
      cta_kind: 'sign_in',
      next_path: currentPath,
      is_returning_device: hasEverAuthenticated,
    });
    const params = new URLSearchParams();
    if (currentPath && currentPath !== '/') {
      params.set('next', currentPath);
    }
    params.set('source', 'drawer_profile');
    params.set('gate_reason', 'generic_auth_entry');
    const signInUrl = `/signin?${params.toString()}`;
    onClose('auth_sign_in_click');
    navigate(signInUrl);
  };

  // Navigate to sign-up page with current location as next
  const handleSignUpClick = () => {
    const currentPath = location.pathname + location.search;
    trackUiElementClicked({
      source_surface: 'drawer_menu',
      surface: 'drawer_profile',
      element_id: 'sign_up_cta',
      action: 'click',
      target: currentPath,
    });
    trackAuthCtaClicked({
      source_surface: 'drawer_profile',
      gate_reason: 'generic_auth_entry',
      cta_kind: 'sign_up',
      next_path: currentPath,
      is_returning_device: hasEverAuthenticated,
    });
    const params = new URLSearchParams();
    if (currentPath && currentPath !== '/') {
      params.set('next', currentPath);
    }
    params.set('source', 'drawer_profile');
    params.set('gate_reason', 'generic_auth_entry');
    const signUpUrl = `/signup?${params.toString()}`;
    onClose('auth_sign_up_click');
    navigate(signUpUrl);
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose('escape_key');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap for accessibility
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [isOpen]);

  const handleItemClick = (item) => {
    trackUiElementClicked({
      source_surface: 'drawer_menu',
      surface: 'drawer_menu',
      element_id: item.label || item.href || 'unknown_item',
      action: 'click',
      target: item.href || null,
    });
    if (item.onClick) {
      item.onClick();
    }
    if (item.href) {
      navigate(item.href);
    }
    onClose('menu_item_click');
  };

  const handleAccountClick = () => {
    trackUiElementClicked({
      source_surface: 'drawer_menu',
      surface: 'drawer_profile',
      element_id: 'account_entry',
      action: 'open_account',
    });
    navigate('/account');
    onClose('account_click');
  };

  const renderMenuItem = (item, index, depth = 0) => {
    if (item.type === 'divider') {
      return <div key={`divider-${index}`} className={styles.divider} />;
    }

    if (item.type === 'section') {
      return (
        <div key={`section-${index}`} className={styles.section}>
          {item.label && (
            <div className={styles.sectionLabel}>{item.label}</div>
          )}
          <div className={styles.sectionItems}>
            {item.items?.map((subItem, subIndex) => 
              renderMenuItem(subItem, `${index}-${subIndex}`, depth)
            )}
          </div>
        </div>
      );
    }

    // Helper to render icon (supports both FA class strings and React components)
    const renderIcon = (icon) => {
      if (!icon) return null;
      if (typeof icon === 'string') {
        return <span className={styles.itemIcon}><FaIcon className={icon} /></span>;
      }
      // React component
      const IconComponent = icon;
      return <span className={styles.itemIcon}><IconComponent size={16} /></span>;
    };

    // Item with nested subsections
    if (item.items && item.items.length > 0) {
      return (
        <div key={`nested-${index}`} className={styles.nestedGroup}>
          <div 
            className={`${styles.menuItem} ${styles.hasSubitems}`}
            style={{ paddingLeft: `${16 + depth * 16}px` }}
          >
            {renderIcon(item.icon)}
            <span className={styles.itemLabel}>{item.label}</span>
          </div>
          <div className={styles.subsection}>
            {item.items.map((subItem, subIndex) => 
              renderMenuItem(subItem, `${index}-sub-${subIndex}`, depth + 1)
            )}
          </div>
        </div>
      );
    }

    // Regular menu item - use Link for items with href, button otherwise
    const itemContent = (
      <>
        {renderIcon(item.icon)}
        <span className={styles.itemLabel}>{item.label}</span>
        {item.badge && <span className={styles.badge}>{item.badge}</span>}
      </>
    );

    const itemClassName = `${styles.menuItem} ${item.disabled ? styles.menuItemDisabled : ''}`;

    if (item.href) {
      // If disabled, render as a span instead of a link
      if (item.disabled) {
        return (
          <span
            key={`item-${index}`}
            className={itemClassName}
            style={{ paddingLeft: `${16 + depth * 16}px` }}
          >
            {itemContent}
          </span>
        );
      }
      return (
        <Link
          key={`item-${index}`}
          to={item.href}
          className={itemClassName}
          {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}
          onClick={() => {
            trackUiElementClicked({
              source_surface: 'drawer_menu',
              surface: 'drawer_menu',
              element_id: item.label || item.href || 'unknown_item',
              action: 'click',
              target: item.href,
            });
            if (item.onClick) item.onClick();
            onClose('menu_item_click');
          }}
          style={{ paddingLeft: `${16 + depth * 16}px` }}
        >
          {itemContent}
        </Link>
      );
    }

    return (
      <button
        key={`item-${index}`}
        className={itemClassName}
        onClick={() => handleItemClick(item)}
        disabled={item.disabled}
        style={{ paddingLeft: `${16 + depth * 16}px` }}
      >
        {itemContent}
      </button>
    );
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ''}`}
        onClick={() => onClose('overlay_click')}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <nav
        ref={drawerRef}
        className={`${styles.drawer} ${isOpen ? styles.drawerOpen : ''}`}
        aria-hidden={!isOpen}
        tabIndex={-1}
      >
        {/* User Profile Section */}
        {showProfileSection && (
          <div className={styles.profileSection}>
            {isSignedIn ? (
              // Signed-in user profile
              <div className={styles.profileHeader}>
                <div className={styles.profileAvatar}>
                  <img 
                    src={user?.imageUrl || profileAvatar} 
                    alt="Profile" 
                    className={styles.profileImage} 
                  />
                </div>
                <div className={styles.profileDetails}>
                  <span className={styles.profileName}>
                    {user?.fullName || user?.firstName || 'Utente'}
                  </span>
                  <span className={styles.profileEmail}>
                    {user?.primaryEmailAddress?.emailAddress || ''}
                  </span>
                </div>
                <button 
                  type="button" 
                  className={styles.accountButton}
                  onClick={handleAccountClick}
                  aria-label="Vai al tuo account"
                >
                  <FaIcon className="fas fa-chevron-right"  />
                </button>
              </div>
            ) : (
              // Anonymous user - show generic info to avoid confusion (they're not logged in)
              <>
                <div className={styles.profileHeader}>
                  <div className={styles.profileAvatar}>
                    <img src={profileAvatar} alt="Profile" className={styles.profileImage} />
                  </div>
                  <div className={styles.profileDetails}>
                    <span className={styles.profileName}>Utente ospite</span>
                  </div>
                  <button 
                    type="button" 
                    className={styles.accountButton}
                    onClick={handleAccountClick}
                    aria-label="Vai al tuo account"
                  >
                    <FaIcon className="fas fa-chevron-right"  />
                  </button>
                </div>
                {/* Auth options - only show sign in if device has authenticated before */}
                <div className={styles.authButtonsContainer}>
                  {hasEverAuthenticated ? (
                    /* Returning user - only show sign in */
                    <button 
                      type="button" 
                      className={styles.authButtonPrimary}
                      onClick={handleSignInClick}
                    >
                      Accedi al tuo account
                    </button>
                  ) : (
                    /* New device - show both options */
                    <>
                      <button 
                        type="button" 
                        className={styles.authButtonPrimary}
                        onClick={handleSignUpClick}
                      >
                        Registrati
                      </button>
                      <button 
                        type="button" 
                        className={styles.authButtonSecondary}
                        onClick={handleSignInClick}
                      >
                        Accedi
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Menu Content */}
        <div className={styles.drawerContent}>
          {menuItems.map((item, index) => renderMenuItem(item, index))}
        </div>

        {/* Footer */}
        <div className={styles.drawerFooter}>
          <span className={styles.footerText}>Giochini di Parole</span>
        </div>
      </nav>
    </>
  );
};

export default DrawerMenu;
